import React, { useEffect, useRef, useReducer } from "react";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import { getAuthToken } from "../api/api";
import { getUserDetails } from "../components/sidebar/Sidenav";
import Tooltip from "../components/common/ToolTip";
import {
  getAPIUrl,
  ValidateInputField,
  notify,
  extractComponentCode,
  validateFirmwareVersionWithComponentCode,
  validateFileNameWithFirmwareVersion,
} from "../components/common/helpers";
import "../styles/Modal.css";
import { ReactComponent as Close } from "../../src/icons-svg/icons/close.svg";
import { Spinner } from "../../src/components/common/Spinner";
import { DraggableModalDialog } from "../components/common/DraggableModal";

const initialContoller = new AbortController();

// Custom hook to preserve cursor position for text inputs
const useCursorPosition = (inputRef, dependencyValue) => {
  const cursorPositionRef = useRef(0);

  const wrapOnChange = (onChangeHandler) => (event) => {
    // Capture cursor position before the onChange handler processes the value
    cursorPositionRef.current = event.target.selectionStart;
    onChangeHandler(event);
  };

  useEffect(() => {
    if (inputRef?.current && cursorPositionRef.current !== null) {
      setTimeout(() => {
        const cursorPos = cursorPositionRef.current;
        inputRef.current.setSelectionRange(cursorPos, cursorPos);
      }, 0);
    }
  }, [dependencyValue]);

  return wrapOnChange;
};

const Upload = (props) => {
  const { getFirmware, disabledFeatures, progressBar, isEdit, setEdit, rowEntries } = props;
  const inputRef = useRef(null);
  const controller = new AbortController();

  // Create refs for all text input fields that need cursor position preservation
  const firmwareVersionInputRef = useRef(null);
  const firmwareDescriptionInputRef = useRef(null);

  const formReducer = (state, action) => {
    switch (action.type) {
      case "SET_FIELD_VALUE":
        return {
          ...state,
          formData: {
            ...state.formData,
            [action.field]: action.value,
          },
        };

      case "SET_VALIDATED":
        return {
          ...state,
          validated: action.validated,
        };
      case "SET_SELECTED_VALUE":
        return {
          ...state,
          selectedValue: action.selectedValue,
        };
      case "SET_SELECTED_FILE":
        return {
          ...state,
          selectedFile: action.selectedFile,
        };
      case "SET_RELEASE_NOTES_NAME":
        return {
          ...state,
          release_notes_name: action.release_notes_name,
        };
      case "SET_SELECT_VALUE":
        return {
          ...state,
          selectValue: action.selectValue,
        };

      case "SET_SHOW":
        return {
          ...state,
          show: action.show,
        };
      case "SET_OPTION":
        return {
          ...state,
          options: action.options,
        };
      case "SET_FORM_ERRORS":
        return {
          ...state,
          formErrors: {
            ...state.formErrors,
            [action.field]: action.error,
          },
        };
      case "SET_VALUE":
        return {
          ...state,
          option: action.option,
        };
      case "LOADING":
        return {
          ...state,
          loading: action.loading,
        };
      case "BLOCK_LIST":
        return {
          ...state,
          blockIdList: action.blockIdList,
        };
      case "RESET_FORM":
        return initialState;
      case "ABORT_TOKEN_RESET":
        return {
          ...state,
          abort: action.abort,
        };
      case "SET_COMPONENT_CODE":
        return {
          ...state,
          componentCode: action.componentCode,
          componentName: action.componentName,
        };
      default:
        return state;
    }
  };
  const initialState = {
    formData: {
      file_name: isEdit ? rowEntries.firmware_name : "",
      firmware_version: isEdit ? rowEntries.firmware_version : "",
      firmware_description: isEdit ? rowEntries.firmware_Description : "",
      release_notes: null,
      File: isEdit ? rowEntries.file_name : null,
      incompatibility: "",
    },
    formErrors: {
      file_name: "",
      firmware_version: "",
      release_notes: "",
      File: "",
    },
    option: [],
    validated: false,
    options: [],
    show: false,
    selectedFile: null,
    release_notes_name: isEdit ? rowEntries.release_notes_name : null,
    loading: false,
    selectedValue: isEdit ? rowEntries.component_type_id : "",
    blockIdList: "",
    selectValue: isEdit ? rowEntries.incompatibility.split(",") : "",
    error: "",
    abort: initialContoller,
    componentCode: "",
    componentName: "",
  };

  const [state, dispatch] = useReducer(formReducer, initialState);

  const {
    formData,
    validated,
    selectedValue,
    options,
    show,
    option,
    selectValue,
    selectedFile,
    loading,
    abort,
    formErrors,
    release_notes_name,
    componentCode,
    componentName,
  } = state;

  // Setup cursor position preservation for text input fields
  const wrapFirmwareVersionChange = useCursorPosition(firmwareVersionInputRef, formData.firmware_version);
  const wrapFirmwareDescriptionChange = useCursorPosition(firmwareDescriptionInputRef, formData.firmware_description);

  // Large file upload
  const chunkSize = 1048576 * 4; // 4 MB chunks
  const blockIdPrefix = "block";

  const formid = useRef(null);
  const handleClose = () => {
    dispatch({ type: "SET_SHOW", show: false });
    dispatch({ type: "SET_SELECT_VALUE", selectValue: [] });
    dispatch({ type: "RESET_FORM" });
    dispatch({ type: "LOADING", loading: false });
    formid.current.classList.remove("blur-effect");
    isEdit && setEdit(false);
    if (abort || controller) {
      abort.abort();
      controller.abort();
    }
  };

  const handleAlert = () => {
    const userAction = window.confirm("This will terminate the upload process. Are you sure you want to proceed?");
    userAction && handleClose();
  };

  const handleShow = () => dispatch({ type: "SET_SHOW", show: true });

  //Handle validations for form fields

  const getUserid = getUserDetails().username;

  const defaultValidation = () => {
    return (Object.values(formErrors).every((error) => error === "valid" || error === "") &&
      // formData.file_name &&
      formData.firmware_version &&
      formData.File &&
      !loading)
  }

  // determine the value for submit button enabling
  const isFormValid = () => {
    if (isEdit) {
      return (defaultValidation() && (JSON.stringify(formData) !== JSON.stringify(initialState["formData"]) ||
        JSON.stringify(selectValue) !== JSON.stringify(initialState.selectValue) ||
        JSON.stringify(selectedValue) !== JSON.stringify(initialState.selectedValue)))
    } else {
      return defaultValidation() && formData.release_notes;
    }
  };

  // handle input change of firmware name, version and file fields
  const handleInputChange = (event) => {
    const { name, value, type } = event.target;

    // Process text fields (firmware_version, file_name, firmware_description)
    let processedValue = value;
    let error = "";

    // Process firmware_version with production_ prefix handling
    if (name === "firmware_version") {
      let cleanValue = value.replace(/\s/g, "");
      const lower = cleanValue.toLowerCase();

      if (lower.startsWith("production_")) {
        const remaining = cleanValue.substring("production_".length);
        processedValue = "production_" + remaining.toUpperCase();
      } else {
        processedValue = cleanValue.toUpperCase();
      }

      // Validate firmware version against component code
      if (componentCode) {
        error = validateFirmwareVersionWithComponentCode(processedValue, componentCode, componentName);
      }

      // Re-validate file if firmware version changes and a file is already selected
      // if (selectedFile) {
      //   const fileValidationError = validateFileNameWithFirmwareVersion(
      //     selectedFile.name,
      //     processedValue
      //   );
      //   dispatch({ type: "SET_FORM_ERRORS", field: "File", error: fileValidationError });
      // }
      dispatch({ type: "SET_FORM_ERRORS", field: name, error: error });
      dispatch({ type: "SET_FIELD_VALUE", field: name, value: processedValue });
    }

    // Handle File upload - isolated logic with early return
    if (name === "File") {
      const selectedFile = event.target.files[0];
      dispatch({
        type: "SET_SELECTED_FILE",
        selectedFile: selectedFile,
      });

      if (selectedFile) {
        const error = validateFileNameWithFirmwareVersion(
          selectedFile.name,
          formData.firmware_version
        );
        dispatch({ type: "SET_FIELD_VALUE", field: name, value: selectedFile });
        dispatch({ type: "SET_FORM_ERRORS", field: "File", error: error });
      }
      return; // Early return to prevent further processing
    }

    // // Handle release notes file - isolated logic with early return
    if (name === "release_notes" && type === "file") {
      if (isEdit) {
        dispatch({
          type: "SET_RELEASE_NOTES_NAME",
          release_notes_name: null,
        });
      }

      if (event.target.files[0]) {
        let error = ValidateInputField(name, event.target.files[0].name);
        dispatch({
          type: "SET_FIELD_VALUE",
          field: "release_notes",
          value: event.target.files[0],
        });
        dispatch({ type: "SET_FIELD_VALUE", field: name, value: processedValue });
        dispatch({ type: "SET_FORM_ERRORS", field: name, error });
      }
      return;
    }

    if (name === "firmware_description") {
      processedValue = value.charAt(0).toUpperCase() + value.slice(1);
      dispatch({ type: "SET_FIELD_VALUE", field: name, value: processedValue });
    } 
  };

  const formatToUpperCase = (inputValue) => {
    return inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
  };

  const updateProgressStatus = (status) => {
    progressBar(status);
  }

  let finalChunkUploadResponse = (formData, totalChunks, file_name) => {
    try {
      let blockIdList = "";
      formData.append("created_by", getUserid);
      for (let i = 0; i < totalChunks; i++) {
        blockIdList += btoa((blockIdPrefix + "-" + i).padStart(30));
        if (i < totalChunks - 1) {
          blockIdList += ",";
        }
        dispatch({ type: "BLOCK_LIST", blockIdList: blockIdList });
      }
      formData.append("blockIdList", blockIdList);
      const finalResponse = axios({
        method: "POST",
        url:
          getAPIUrl("uploadFirmwareDetails") +
          `?mode=blockList&blobName=${file_name}`,
        headers: {
          Authorization: getAuthToken(),
          "Content-Type": "application/form-data",
        },
        signal: controller.signal,
        data: formData,
      });
      finalResponse
        .then((res) => {
          dispatch({ type: "LOADING", loading: false });
          if (!res) return alert("Error saving the file");
          if (res.status === 200 && res.data === "file uploaded successfully") {
            handleClose();
            updateProgressStatus(true);
            alert("Firmware Details Added Successfully");
            setTimeout(() => {
              getFirmware();
              updateProgressStatus(false);
            }, 5000);
          } else {
            alert("Error saving the File");
            formid.current.classList.remove("blur-effect");
          }
        })
        .catch((error) => {
          dispatch({ type: "LOADING", loading: false });
          error.message !== "canceled" && notify("submission");
        });
    } catch (error) {
      console.log("Error uploading the complete file", error);
      alert("Error saving the file due to:" + error);
    }
  };

  // Handle single chunk Data and API requuest
  const uploadChunk = async (index, totalSize, file_name) => {
    const start = index * chunkSize;
    const end = Math.min(start + chunkSize, totalSize);
    const chunk = selectedFile.slice(start, end);
    let blockId = btoa((blockIdPrefix + "-" + index).padStart(30));

    try {
      const response = await axios({
        method: "POST",
        signal: abort.signal,
        url:
          getAPIUrl("uploadFirmwareDetails") +
          `?mode=block&blockId=${blockId}&blobName=${file_name}`,
        headers: {
          Authorization: getAuthToken(),
          "Content-Type": "multipart/form-data",
        },
        data: chunk,
      });
      return response.data;
    } catch (error) {
      if (formid.current?.classList.remove("blur-effect"))
        if ((controller || abort || error.message === 'canceled')) {
          abort.abort()
          controller.abort()
        }
    }
  };

  // Handle the chunk upload api calls concurrently
  const uploadChunkBatches = async (
    formData,
    totalChunks,
    totalSize,
    file_name
  ) => {
    const concurrency = 3;
    const chunkPromises = [];
    dispatch({ type: "LOADING", loading: true });
    formid?.current?.classList.add("blur-effect");
    for (let index = 0; index < totalChunks; index += concurrency) {
      if (formid?.current === null) {
        break;
      } const chunkBatch = Array.from(
        { length: concurrency },
        (val, i) => index + i
      ).filter((i) => i < totalChunks);
      const promises = chunkBatch.map((id) =>
        uploadChunk(id, totalSize, file_name)
      );
      chunkPromises.push(await Promise.allSettled(promises));
      await Promise.all(chunkPromises);
    }
    formData.delete("File");
    finalChunkUploadResponse(formData, totalChunks, file_name);
    dispatch({ type: "SET_VALIDATED", validated: false });
  };

  const checkForExistingFile = (event) => {
    if (!selectedFile) return;
    const { name: file_name, size: totalSize } = selectedFile;

    try {
      axios({
        method: "GET",
        url: getAPIUrl("getFileAlreadyExists") + `?blobName=${file_name}&firmware_version=${formData.firmware_version}&component_type_id=${selectedValue}`,
        headers: {
          Authorization: getAuthToken(),
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (response.data.code === 5030) {
            alert("Firmware Name, Firmware Version And Component Type Selected Already Exists");
          } else if (response.data.code === 5032) {
            alert("Firmware Details Already Exist");
          } else if (response.data.code === 5031 || response.data.code === 5033) {
            alert("File Name Already Exists");
          } else if (response.data.code === 5034) {
            alert("Since the same Firmware File has already been uploaded to our repository, kindly consider renaming the File.");
          } else {
            const formData = new FormData(event.target);
            // calculate the total number of chunks
            const totalChunks =
              totalSize % chunkSize === 0
                ? totalSize / chunkSize
                : Math.floor(totalSize / chunkSize) + 1;

            uploadChunkBatches(formData, totalChunks, totalSize, file_name);
          }
        })
        .catch((error) => {
          error.message !== "canceled" && notify("submission");
        });
    } catch (error) {
      console.log("error in the api call" + error);
    }
  }

  const updateFirmwareDetails = (event) => {
    const formData = new FormData(event.target);
    formData.append("file_Id", rowEntries.file_id);
    if (release_notes_name !== null) {
      formData.delete("release_notes");
      formData.append("release_notes_name", release_notes_name);
    }
    formData.append("updated_by", getUserDetails().username);
    try {
      axios({
        method: "PATCH",
        url: getAPIUrl("updateFirmwareDetails"),
        headers: {
          Authorization: getAuthToken(),
        },
        signal: controller.signal,
        data: formData,
      })
        .then((response) => {
          if (response.data.code === 200) {
            handleClose();
            alert("Firmware Details Updated Successfully");
            setTimeout(() => {
              getFirmware();
            }, 3000);
          }
        })
        .catch((error) => {
          error.message !== "canceled" && notify("updating");
        });
    } catch (error) {
      console.log("error in the api call" + error);
    }
  }

  const handleSubmit = async (event) => {
    dispatch({ type: "ABORT_TOKEN_RESET", abort: initialContoller });
    event.preventDefault();
    event.stopPropagation();
    if (isEdit) {
      updateFirmwareDetails(event);
    } else {
      checkForExistingFile(event);
    }
  };

  const setSelectedValue = () => {
    if (isEdit && isEdit !== undefined) {
      return options.filter((obj) =>
        selectedValue === obj.value
      )
    } else if (selectedValue !== "") {
      return options.filter((obj) => selectedValue === obj.value);
    } else {
      return options[0];
    }
  };

  useEffect(() => {
    if (show || isEdit) {
      inputRef.current.focus();
      const abortController = new AbortController();
      dispatch({ type: "ABORT_TOKEN_RESET", abort: abortController });
      axios({
        method: "GET",
        url: getAPIUrl("getComponentTypeApi"),
        headers: {
          Authorization: getAuthToken(),
        },
        signal: controller.signal,
      })
        .then((res) => {
          const formateOptions = res.data.map((item) => ({
            value: item.component_type_id,
            label: item.component_type,
          }));
          dispatch({ type: "SET_OPTION", options: formateOptions });
          !isEdit && setDefaultValue(formateOptions)
        })
        .catch((error) => {
          dispatch({ type: "SET_OPTION", options: [] });
          console.log(error);
        });
    }

    return () => {
      controller.abort();
    };
  }, [show]);

  const setDefaultValue = (formateOptions) => {
    dispatch({
      type: "SET_SELECTED_VALUE",
      selectedValue: formateOptions[0].value,
    });
  };

  useEffect(() => {
    if ((show || isEdit) && selectedValue) {
      axios({
        method: "GET",
        url:
          getAPIUrl("getIncompatibilityListApi") +
          `?componentTypeId=${selectedValue}`,
        headers: {
          Authorization: getAuthToken(),
        },
        signal: controller.signal,
      })
        .then((res) => {
          let formatOption;
          if (res && res.data.code !== 404) {
            formatOption = res.data.map((item) => ({
              value: item,
              label: item,
            }));
          } else {
            formatOption = [];
          }
          dispatch({ type: "SET_VALUE", option: formatOption });
        })
        .catch((error) => {
          dispatch({ type: "SET_VALUE", option: [] });
          console.log(error);
        });
    }
  }, [show, selectedValue, isEdit]);

  // Extract component code from selected component type
  useEffect(() => {
    if (selectedValue && options.length > 0) {
      const selectedOption = options.find((opt) => opt.value === selectedValue);
      if (selectedOption) {
        const code = extractComponentCode(selectedOption.label);
        const componentName = selectedOption.label.split(" - ")[0].trim();
        dispatch({ type: "SET_COMPONENT_CODE", componentCode: code, componentName: componentName });
        // Re-validate firmware version if it exists
        if (formData.firmware_version) {
          const validationResult = validateFirmwareVersionWithComponentCode(
            formData.firmware_version,
            code,
            componentName
          );
          dispatch({
            type: "SET_FORM_ERRORS",
            field: "firmware_version",
            error: validationResult
          });
        }
      }
    }
  }, [selectedValue, options]);

  const handleChanged = (e) => {
    dispatch({ type: "SET_SELECTED_VALUE", selectedValue: e.value });
  };

  // handle onChange event of the dropdown
  const handleChange = (e) => {
    let incompatibilityList = [];
    Object.values(e).forEach((f) => incompatibilityList.push(f.value));
    dispatch({ type: "SET_SELECT_VALUE", selectValue: incompatibilityList });
  };

  return (
    <>
      {" "}
      {!isEdit && (!disabledFeatures.includes("AddFirmware")) && (
        <Button
          style={{ background: "#ffffff", color: "#000000", width: "140px" }}
          size="sm"
          variant="secondary"
          onClick={handleShow}
          disabled={disabledFeatures.includes("AddFirmware")}
        >
          +Add a Firmware
        </Button>
      )}
      <Modal
        show={show || isEdit}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        dialogAs={DraggableModalDialog}
      >
        <Modal.Header className="cancelButton">
          <Modal.Title style={{ color: "#ffffff" }}>
            <Row>
              <Col sm={{ span: 7 }}>
                <h3>{isEdit !== null && isEdit ? "Edit Firmware Details" : "Add Firmware"}</h3>{" "}
              </Col>
              <Col sm={{ offset: 3 }}>
                <Button
                  className="col-md-8 hoverEffect"
                  size="sm"
                  variant="Dark"
                  onClick={loading ? handleAlert : handleClose}
                  aria-label="Close Button"
                >
                  <Close />
                </Button>
              </Col>
            </Row>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#212529" }}>
          <div>
            <Form
              noValidate
              validated={validated}
              onSubmit={handleSubmit}
              ref={formid}
            >
              <Row className="mb-3">
                <Form.Group as={Row} md="12" className="formField">
                  <Form.Label
                    className="required"
                    ref={inputRef}
                    style={{ color: "white" }}
                    size="sm"
                    column
                    sm={4}
                    htmlFor="firmware version"
                  >
                    Firmware Version
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      ref={firmwareVersionInputRef}
                      size="sm"
                      aria-required
                      type="text"
                      placeholder="Firmware version"
                      id="validationCustom02"
                      name="firmware_version"
                      onChange={wrapFirmwareVersionChange(handleInputChange)}
                      readOnly={loading}
                      value={formData.firmware_version}
                      aria-invalid={formErrors.firmware_version === "error"}
                      isInvalid={formErrors.firmware_version === "error"}
                      isValid={formErrors.firmware_version === "valid"}
                    />
                    {formErrors.firmware_version === "error" && componentCode && (
                      <Tooltip type="version" specialCharException={"_-"} required={true} code={componentCode} componentName={componentName} />
                    )}
                    {componentCode && (
                      <Form.Text className="text-muted d-block mt-2">
                        <div style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
                          <strong>Instructions:</strong>
                          Firmware version must include component code: <strong>{componentCode}</strong>
                          {componentName?.toUpperCase().includes("COMPASS FIXTURE") && (
                            <>
                              <br />
                              <span>For Compass Fixture components, the prefix <strong>production_</strong> is required.</span>
                            </>
                          )}
                          <br />
                          <span>Example: {componentName?.toUpperCase().includes("COMPASS FIXTURE") ? `production_AAA${componentCode}TYU-V09` : `AAA${componentCode}TYU-V09`}</span>
                        </div>
                      </Form.Text>
                    )}
                  </Col>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group as={Row} md="12" className="formField">
                  <Form.Label
                    style={{ color: "white" }}
                    size="sm"
                    column
                    sm={4}
                    htmlFor="firmware description"
                  >
                    Firmware Description
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      ref={firmwareDescriptionInputRef}
                      size="sm"
                      required
                      readOnly={loading}
                      as="textarea"
                      placeholder="Firmware description"
                      id="validationCustom03"
                      name="firmware_description"
                      value={
                        formData.firmware_description !== ""
                          ? formatToUpperCase(formData.firmware_description)
                          : formData.firmware_description
                      }
                      onChange={wrapFirmwareDescriptionChange(handleInputChange)}
                    />
                  </Col>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group
                  as={Row}
                  md="12"
                  name="componenttype_id"
                  className="formField"
                >
                  <Form.Label
                    className="required"
                    style={{ color: "white" }}
                    size="sm"
                    column
                    sm={4}
                    htmlFor="component type"
                  >
                    Component Type
                  </Form.Label>
                  <Col sm={8}>
                    <Select
                      size="sm"
                      name="componenttype_id"
                      onChange={handleChanged}
                      aria-required
                      disabled={loading}
                      value={setSelectedValue()}
                      options={options}
                    >
                    </Select>
                  </Col>
                  <Col sm={12}>
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid component type.
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </Row>
              <Row className="mb-3 ">
                <Form.Group
                  as={Row}
                  md="12"
                  className="formField"
                >
                  <Form.Label
                    style={{ color: "white" }}
                    size="sm"
                    column
                    sm={4}
                    htmlFor="incompatibility"
                  >
                    Incompatibility
                  </Form.Label>
                  <Col sm={8}>
                    <Select
                      size="sm"
                      isDisabled={loading}
                      value={option.filter((obj) =>
                        selectValue.includes(obj.value)
                      )} // set selected values
                      options={option} // set list of the data
                      onChange={handleChange} // assign onChange function
                      isMulti
                      isClearable
                      name="incompatibility"
                    />
                  </Col>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group
                  as={Row}
                  md="12"
                  className="formField"
                >
                  <Form.Label
                    className={"required"}
                    style={{ color: "white" }}
                    size="sm"
                    column
                    sm={4}
                    htmlFor="release notes"
                    aria-label="release notes"
                  >
                    Release Notes
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      size="sm"
                      aria-required
                      type="file"
                      id="validationCustom07"
                      name="release_notes"
                      onChange={handleInputChange}
                      aria-invalid={formErrors.release_notes === "error"}
                      isInvalid={formErrors.release_notes === "error"}
                      isValid={formErrors.release_notes === "valid"}
                    />
                    {(isEdit && release_notes_name !== null) && <Form.Text className="text-muted">{rowEntries.release_notes_name}</Form.Text>}
                    {formErrors.release_notes === "error" && (
                      <Tooltip type="release_notes" />
                    )}
                  </Col>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group
                  as={Row}
                  md="12"
                  className="formField"
                >
                  <Form.Label
                    className={"required"}
                    style={{ color: "white" }}
                    size="sm"
                    column
                    sm={4}
                    htmlFor="file upload"
                    aria-label="file upload"
                  >
                    File Upload
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      className={isEdit && "notallowed"}
                      size="sm"
                      disabled={loading || isEdit}
                      aria-required
                      type="file"
                      id="validationCustom05"
                      name="File"
                      onChange={handleInputChange}
                      value={(isEdit) && File}
                      aria-invalid={formErrors.File === "error"}
                      isInvalid={formErrors.File === "error"}
                      isValid={formErrors.File === "valid"}
                    />
                    {(isEdit && File !== null) && <Form.Text className="text-muted">{rowEntries.file_name}</Form.Text>}

                    {formErrors.File === "error" && selectedFile && (
                      <Tooltip type="file" firmware_version={formData.firmware_version} specialCharException="-, _" />
                    )}
                    {(formData.firmware_version && formErrors.firmware_version !== "error") && (
                      <Form.Text className="text-muted d-block mt-2">
                        <div style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
                          {/* <strong>Instructions:</strong> File name must exactly match the firmware version (case-sensitive) with file extension
                          <br /> */}
                          <span>Allowed extensions: tar, zip</span>
                          <br />
                          <span>Example: {formData.firmware_version}.tar</span>
                        </div>
                      </Form.Text>
                    )}
                  </Col>
                </Form.Group>
              </Row>
              <Row>
                <Col sm={{ span: 10, offset: 2 }}>
                  <Button
                    className="col-md-5"
                    size="sm"
                    variant="secondary"
                    type="submit"
                    disabled={!isFormValid()}
                  >
                    {(isEdit !== null && isEdit) ? "Update" : "Submit"}
                  </Button>{" "}
                  <Button
                    className="col-md-5"
                    size="sm"
                    variant="secondary"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
            {loading && (
              <div style={{ zIndex: "2000" }}>
                <Spinner top="40%" color="grey" />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Upload;
